import { createContext, useContext, useEffect, useState } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { initializeApp, getApps } from "firebase/app";

/* ---------------- Firebase Init ---------------- */

const firebaseConfig = {
  apiKey: "AIzaSyCV-_rLIMlLVbkTzikYPPzYE_wh0plFVx4",
  authDomain: "edalyze-e6041.firebaseapp.com",
  projectId: "edalyze-e6041",
  storageBucket: "edalyze-e6041.firebasestorage.app",
  messagingSenderId: "188208886272",
  appId: "1:188208886272:web:cfea9e3ca81266ed191ee5",
  measurementId: "G-SKRQTBQR0Z"
};

// ✅ Prevent double init
if (!getApps().length) {
  initializeApp(firebaseConfig);
}

const auth = getAuth();

/* ---------------- Context ---------------- */

const AuthContext = createContext(null);

/* ---------------- Provider ---------------- */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (!firebaseUser) {
          setUser(null);
          setLoading(false);
          return;
        }

        const token = await firebaseUser.getIdToken();

        const res = await fetch("/api/auth/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        });

        // 🔒 Backend rejected token
        if (!res.ok) {
          await signOut(auth);
          setUser(null);
          setLoading(false);
          return;
        }

        const profile = await res.json();
        setUser(profile);
        setLoading(false);
      } catch (err) {
        console.error("Auth check failed:", err);
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  /* ---------------- Actions ---------------- */

  const signup = async (email, password, profileData = {}) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();

    await fetch("/api/auth/profile", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      body: JSON.stringify(profileData),
    });

    return cred;
  };

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const loginWithGoogle = () => signInWithPopup(auth, new GoogleAuthProvider());

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const isAuthenticated = !!user;

  /* ---------------- Provider ---------------- */

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        signup,
        login,
        loginWithGoogle,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/* ---------------- Hook ---------------- */

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
};
